import escapeStringRegexp from 'escape-string-regexp'
import { flatten } from 'flat'
import { isEmpty } from 'lodash'

import formMessages from '../../i18n/locales/default.json'

type FlatRecordKeys<T extends Record<string, unknown>, Key = keyof T> = Key extends string
  ? T[Key] extends Record<string, unknown>
    ? `${Key}.${FlatRecordKeys<T[Key]>}`
    : `${Key}`
  : never

export type FlatFormMessageKeys = FlatRecordKeys<typeof formMessages>

const flattenedMessages = flatten(formMessages) as Record<FlatFormMessageKeys, string>

type YupErrorMessageValue = string | number | RegExp
type YupErrorMessageValues = Record<string, YupErrorMessageValue>

/**
 * Derive an expected message from the Yup error message key.
 *
 * @param key Dot notation key for the message in the formMessages object.
 * @param values Explicit placeholder values from the Yup validation context.
 * @returns A "good enough" error message pattern that replaces placeholders
 * with the provided values and falls back to a wildcard for the rest.
 *
 * @see `libs/forms/src/lib/i18n/locales/default.json` for the full list of Yup error messages.
 */
export const getYupErrorMessage = (key: FlatFormMessageKeys, values?: YupErrorMessageValues) => {
  const message = flattenedMessages[key]

  if (!values || isEmpty(values)) {
    return new RegExp(message.replace(/{.*?}/g, '.*'))
  }

  const patternWithValues = Object.entries(values).reduce((acc, [placeholder, value]) => {
    const replacement = value instanceof RegExp ? value.source : escapeStringRegexp(String(value))
    const placeholderPattern = new RegExp(`\\{${escapeStringRegexp(placeholder)}\\}`, 'g')
    return acc.replace(placeholderPattern, () => replacement)
  }, message)

  const finalPattern = patternWithValues.replace(/{.*?}/g, '.*')

  return new RegExp(finalPattern)
}
